import { transparentize } from 'polished';
import React, { useRef } from 'react';
import styled from 'styled-components';
import { getIsBreakpointGreaterThan } from '@peloton/responsive/reducer';
import { defaultTransition } from '@peloton/styles/animation';
import { media } from '@peloton/styles/breakpoints';
import { white, gray4, black, slate3 } from '@engage/colors/base';
import { If } from '@engage/conditional-render/If';
import { useReduxState, useReduxAction } from '@engage/redux/hooks';
import { spaces } from '@engage/styles/spaces';
import { label16 } from '@engage/typography/font-styles';
import {
  MembersCopy,
  useMembers,
  useFacebook,
  useAria,
} from '@members/copy/copy.generated';
import { Aria } from '@members/copy/models.generated/Aria';

import { Facebook } from '@members/copy/models.generated/Facebook';
import { Members } from '@members/copy/models.generated/Members';
import { FacebookIconBlue } from '@members/facebook/icons/FacebookIconBlue';
import { findFacebookFriends } from '@members/facebook/redux/facebookConnect';
import { LightLabelA11y } from '@members/form/label';
import CancelIcon from '@members/icons/CancelIcon';
import { MembersListModalHeader } from '@members/members/membersListModal';

import SearchIconSimple from './icons/SearchIconSimple';

export type SearchControlProps = {
  inputValue: string;
  handleInputChange(e: React.FormEvent<HTMLInputElement>): void;
  handleClickClear(): void;
  handleFbClick?(): void;
};

const isEmpty = (s: string) => (s?.length ?? 0) === 0;

export const MemberSearchControls: React.FC<
  React.PropsWithChildren<SearchControlProps>
> = props => {
  const inputRef = useRef<HTMLInputElement>(null);
  const facebookLabel = useFacebook(Facebook.SearchButtonCta);
  const clearSearchAriaLabel = useAria(Aria.ClearSearch);
  const findFbFriends = useReduxAction(findFacebookFriends);

  return (
    <>
      <Header />
      <Controls>
        <InputContainer
          data-test-id="searchForm"
          theme={props.inputValue.length === 0 ? 'placeholder' : 'standard'}
        >
          <If condition={isEmpty(props.inputValue)}>
            <SearchInputIcon />
          </If>
          <Input
            autoComplete="off"
            name="searchTerm"
            data-test-id="userSearchInput"
            type="text"
            value={props.inputValue}
            onChange={props.handleInputChange}
            aria-label={useMembers(Members.SearchInputLabel)}
            ref={inputRef}
          />
          <StyledLabel
            data-test-id="userSearchPlaceholder"
            isEmptyField={isEmpty(props.inputValue)}
            htmlFor={useMembers(Members.SearchInputLabel)}
          >
            <div>{useMembers(Members.SearchPlaceholder)}</div>
          </StyledLabel>
          <ClearButton
            aria-label={clearSearchAriaLabel}
            onClick={() => {
              inputRef.current?.focus();
              props.handleClickClear();
            }}
          >
            <CancelIcon data-test-id="clearIcon" aria-hidden="true" />
          </ClearButton>
        </InputContainer>
        <FacebookButton
          aria-label={facebookLabel}
          onClick={() => {
            (props.handleFbClick as () => void)();
            findFbFriends();
          }}
        >
          <FacebookIcon />
        </FacebookButton>
      </Controls>
    </>
  );
};

const Header: React.FC<React.PropsWithChildren<unknown>> = () => {
  const showPelotonInTitle = useReduxState(getIsBreakpointGreaterThan, 'smallScreen');
  return (
    <MembersListModalHeader>
      <Title data-test-id="searchTitle">
        <MembersCopy
          id={Members.SearchTitle}
          values={{
            showPelotonInTitle,
          }}
        />
      </Title>
    </MembersListModalHeader>
  );
};

const Controls = styled.div`
  display: flex;
  flex-direction: row;
  padding: ${spaces.medium}px;
  padding-bottom: ${spaces.xHuge}px;

  ${media.tablet`
    padding: ${spaces.large}px;
  `}
`;

const Title = styled.h1`
  color: ${slate3};
  ${label16}
`;

const Input = styled.input`
  ${label16}
  background-color: ${white};
  outline: none;
  border: none;
  width: 100%;
  box-shadow: inset 0 1px 0 0 ${transparentize(0.9, black)};
  border-radius: 5px;
  height: 50px;
  padding: ${spaces.xLarge}px 15px 10px 15px;
  &::-ms-clear {
    // IE clear button
    display: none;
    width: 0;
    height: 0;
  }
`;

const StyledLabel = styled(LightLabelA11y)`
  border: solid 1px ${transparentize(0.4, gray4)};
  border-radius: 5px;
  padding-top: ${spaces.xxxSmall}px;
`;

const SearchInputIcon = styled(SearchIconSimple)`
  position: absolute;
  width: 16px;
  height: 16px;
  top: 18px;
  right: 15px;
  ${defaultTransition('fill')}
`;

const ClearButton = styled.button`
  position: absolute;
  cursor: pointer;
  width: 44px;
  height: 44px;
  padding: 12px;
  fill: ${gray4};
  right: 0;
  top: 50%;
  transform: translateY(-50%);
  ${defaultTransition('opacity')}

  svg {
    width: 20px;
    height: 20px;
  }

  ${media.tablet`
    right: 3px;
  `}
`;

const InputContainer = styled.div`
  ${label16}
  position: relative;
  flex: 1;

  ${defaultTransition('border', 400)}
  height: 50px;
  ${ClearButton} {
    opacity: ${(props: { theme: string }) => (props.theme === 'placeholder' ? '0' : '1')};
    visibility: ${(props: { theme: string }) =>
      props.theme === 'placeholder' ? 'hidden' : 'visible'};
  }
  ${SearchInputIcon} {
    fill: ${(props: { theme: string }) =>
      props.theme === 'placeholder' ? gray4 : slate3};
  }
`;

const FacebookButton = styled.button`
  margin-left: ${spaces.large}px;
`;

const FacebookIcon = styled(FacebookIconBlue)`
  height: ${spaces.large}px;
  width: ${spaces.large}px;
`;
